<script setup lang="ts">
import type { Component, CSSProperties } from 'vue';
import type { ConversationItem, ConversationMenu, ConversationMenuCommand } from '../types';
import { MoreFilled } from '@element-plus/icons-vue';

interface Props {
  item: ConversationItem;
  showCheckbox?: boolean;
  checked?: boolean;
  itemsStyle?: CSSProperties;
  itemsHoverStyle?: CSSProperties;
  itemsActiveStyle?: CSSProperties;
  itemsMenuOpenedStyle?: CSSProperties;
  prefixIcon?: Component | null;
  suffixIcon?: Component | null;
  showTooltip?: boolean;
  labelMaxWidth?: number;
  menu?: ConversationMenu[];
  showBuiltInMenu?: boolean;
  alwaysShowBuiltInMenu?: boolean;
  tooltipPlacement?: any;
  tooltipOffset?: number;
  menuPlacement?: any;
  menuOffset?: number;
  menuMaxHeight?: number;
  menuStyle?: CSSProperties;
  menuShowArrow?: boolean;
  menuClassName?: string;
  menuTeleported?: boolean;
  active?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'click', key: string): void;
  (e: 'menuCommand', command: ConversationMenuCommand, item: ConversationItem): void;
  (e: 'toggleCheck', id: string, checked: boolean): void;
}>();
const slots = defineSlots();

const isHovered = ref(false);
const isShowMenuBtn = ref(false);
const elDropdownRef = ref<any>(null);

const isTextOverflow = computed(() => {
  return (label: string = '') => {
    if (!props.labelMaxWidth || typeof window === 'undefined')
      return false;
    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'nowrap';
    span.style.fontSize = '14px';
    span.textContent = label;
    document.body.appendChild(span);
    const textWidth = span.offsetWidth;
    document.body.removeChild(span);
    return textWidth > props.labelMaxWidth;
  };
});

const labelStyle = computed(() => {
  if (props.labelMaxWidth)
    return { maxWidth: `${props.labelMaxWidth}px`, overflow: 'hidden' as const };
  return {};
});

const shouldShowMenu = computed(() => {
  return isHovered.value || props.active || isShowMenuBtn.value;
});

// 自动生成头像：首字母 + 基于标签的稳定颜色
const avatarLetter = computed(() => {
  const item = props.item as any;
  const title = item.label || item.sessionTitle || item.name || item.title || '';
  return title.trim().charAt(0) || '?';
});

const avatarColor = computed(() => {
  const item = props.item as any;
  const label = item.label || item.sessionTitle || item.id || '?';
  let hash = 0;
  for (let i = 0; i < String(label).length; i++) {
    hash = String(label).charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 55%, 52%)`;
});

function handleMouseEnter() { isHovered.value = true; }
function handleMouseLeave() { isHovered.value = false; }
function handleClick() { emit('click', (props.item as any).uniqueKey || props.item.key || ''); }

function updateMenuStatus(isOpen: boolean) {
  isShowMenuBtn.value = isOpen;
  if (!isOpen || typeof window === 'undefined')
    return;

  nextTick(() => {
    const dropdownMenus = document.querySelectorAll('.elx-conversations-dropdown-menu');
    dropdownMenus.forEach((dropdownMenuItem) => {
      if (!dropdownMenuItem || (dropdownMenuItem as HTMLElement).style.display === 'none')
        return;

      // Hide/show arrow
      const arrows = dropdownMenuItem.querySelectorAll('.el-popper__arrow');
      arrows.forEach((arrow) => {
        ;(arrow as HTMLElement).style.display = props.menuShowArrow ? 'block' : 'none';
      });

      // Inject menuItem hover styles
      const items = dropdownMenuItem.querySelectorAll('.el-dropdown-menu__item:not(.is-disabled)');
      if (!items.length || !props.menu?.length)
        return;
      items.forEach((item, index) => {
        const hoverStyle = props.menu![index]?.menuItemHoverStyle;
        if (!hoverStyle)
          return;
        const className = `custom-hover-${index}`;
        item.classList.add(className);
        const cssText = Object.entries(hoverStyle)
          .map(([prop, val]) => `${prop.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${val} !important;`)
          .join(' ');
        const style = document.createElement('style');
        style.textContent = `.${className}:hover,.${className}:focus{${cssText}}`;
        document.head.appendChild(style);
      });
    });
  });
}

function menuCommand(command: string | number | object, item: ConversationItem) {
  if (slots.menu)
    return;
  emit('menuCommand', command, item);
}

function openDropdown() { elDropdownRef.value?.handleOpen?.(); }
function closeDropdown() { elDropdownRef.value?.handleClose?.(); }
</script>

<template>
  <li
    class="elx-conversations-item" :class="[
      item.disabled ? 'elx-conversations-item--disabled' : '',
      active ? 'elx-conversations-item--active' : '',
      item.disabled ? '' : isHovered ? 'elx-conversations-item--hovered' : '',
      isShowMenuBtn ? 'elx-conversations-item--menu-opened' : '',
      alwaysShowBuiltInMenu ? 'elx-conversations-item--always-show-built-in-menu' : '',
    ]"
    :style="{ ...itemsStyle }"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="elx-conversations-item__content">
      <div v-if="showCheckbox" class="elx-conversations-item__checkbox" @click.stop>
        <el-checkbox
          :model-value="checked"
          @change="(v: boolean) => emit('toggleCheck', (item as any).uniqueKey || (item as any).id, v)"
        />
      </div>
      <!--      <span v-if="prefixIcon" class="elx-conversations-item__prefix-icon"> -->
      <!--        <el-icon><component :is="prefixIcon" /></el-icon> -->
      <!--      </span> -->
<!--      <span class="elx-conversations-item__avatar" :style="{ backgroundColor: avatarColor }">{{ avatarLetter }}</span>-->

      <div class="elx-conversations-item__content-main">
        <slot name="default" :item="item">
          <slot name="label">
            <div class="elx-conversations-item__label-container">
              <el-tooltip
                v-if="showTooltip && isTextOverflow((item as any).label)"
                :content="(item as any).label"
                :placement="tooltipPlacement"
                :offset="tooltipOffset"
                effect="dark"
              >
                <span
                  class="elx-conversations-item__label" :class="[
                    isTextOverflow((item as any).label) ? 'elx-conversations-item__label--gradient' : '',
                  ]"
                  :style="labelStyle"
                >{{ (item as any).label }}</span>
              </el-tooltip>
              <span
                v-else
                class="elx-conversations-item__label" :class="[
                  isTextOverflow((item as any).label) ? 'elx-conversations-item__label--gradient' : '',
                ]"
                :style="labelStyle"
              >{{ (item as any).label }}</span>
            </div>
          </slot>
        </slot>
      </div>

      <span v-if="suffixIcon" class="elx-conversations-item__suffix-icon">
        <el-icon><component :is="suffixIcon" /></el-icon>
      </span>

      <div
        class="elx-conversations-item__dropdown-menu-container" :class="[
          { 'elx-conversations-item__dropdown-menu-container--hidden': !((shouldShowMenu && showBuiltInMenu) || alwaysShowBuiltInMenu) },
        ]"
      >
        <div v-show="menu && menu.length" class="elx-conversations-item__dropdown-more" @click.stop>
          <el-dropdown
            ref="elDropdownRef"
            trigger="click"
            :placement="menuPlacement"
            :offset="menuOffset"
            :teleported="menuTeleported"
            :popper-class="menuClassName ? `elx-conversations-dropdown-menu ${menuClassName}` : 'elx-conversations-dropdown-menu'"
            :max-height="menuMaxHeight"
            :disabled="item.disabled"
            @visible-change="updateMenuStatus"
            @command="(cmd: any) => menuCommand(cmd, item)"
          >
            <template #default>
              <slot
                name="more-filled"
                v-bind="{ item, isHovered: item.disabled ? false : isHovered, isActive: active, isMenuOpened: isShowMenuBtn, isDisabled: item.disabled }"
              >
                <el-icon class="elx-conversations-dropdown-more-icon">
                  <MoreFilled />
                </el-icon>
              </slot>
            </template>
            <template #dropdown>
              <slot name="menu" v-bind="{ item, handleOpen: openDropdown, handleClose: closeDropdown }">
                <el-dropdown-menu :style="menuStyle">
                  <el-dropdown-item
                    v-for="menuItem in menu"
                    :key="menuItem.key"
                    :icon="menuItem.icon"
                    :disabled="menuItem.disabled"
                    :divided="menuItem.divided"
                    :command="menuItem.command"
                    :style="menuItem.menuItemStyle"
                  >
                    {{ menuItem.label }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </slot>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
  </li>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
